import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { Card, CardHeader, Typography, CardContent } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { useFormContext } from '../../../hooks/useForm';
import { reorderArray } from '../../../utils/filterData';
import FormInput from '../../../components/FormInputs/FormInput';

QuestionView.propTypes = {
  question: PropTypes.object,
  sectionsList: PropTypes.array,
  page: PropTypes.number,
  setSections: PropTypes.func,
  setPage: PropTypes.func,
  setHasSelectedSection: PropTypes.func,
  section: PropTypes.object,
};

export default function QuestionView({
  question,
  sectionsList,
  page,
  setSections,
  setPage,
  setHasSelectedSection,
  section,
}) {
  const {
    getQuestionOptions,
    modifyQuestion,
    addFiles,
    removeFiles,
    questions: rawQuestions, // raw context — options are still IDs, not objects
  } = useFormContext();

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const optionsList = getQuestionOptions(question.id);
    setOptions(optionsList || []);
  }, [question.id, getQuestionOptions]);

  const handleSelection = (value) => {
    /**
     * CRITICAL: spread rawQuestion (option IDs intact) instead of the transformed
     * `question` prop (option objects). Spreading transformed options back into
     * context corrupts getQuestionOptions and makes all options disappear.
     */
    const rawQuestion = rawQuestions[question.id] || question;
    modifyQuestion(question.id, { ...rawQuestion, inputValue: value });

    // BRANCHING: navigate to the section mapped to this option
    if (question.redirectOnAnswer && value) {
      const selectedOption = options.find((opt) => opt.value === value);
      const targetSectionId = selectedOption?.redirectTo;

      if (targetSectionId && targetSectionId !== '0') {
        const targetIndex = sectionsList.findIndex((s) => s.id === targetSectionId);
        if (targetIndex !== -1) {
          const newOrder = reorderArray(sectionsList, page, targetIndex);
          setSections(newOrder.array);
          setPage(newOrder.currentIndex);
          setHasSelectedSection(true);
        }
      }
    }
  };

  const isCheckboxEmpty = useMemo(
    () =>
      question.type === 'CheckboxInput' &&
      Array.isArray(question.inputValue) &&
      question.inputValue.length === 0,
    [question.type, question.inputValue]
  );

  return (
    <Card
      sx={{ mb: 2.5, boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)', borderRadius: '16px' }}
    >
      <CardHeader
        title={question.text}
        sx={{
          color:
            question.isRequired && (!question.inputValue || isCheckboxEmpty)
              ? 'error.main'
              : 'initial',
          '& .MuiCardHeader-title': {
            fontSize: isSmallScreen ? '0.875rem' : '1rem',
            fontWeight: 600,
          },
        }}
      />
      <CardContent>
        <FormInput
          input={question}
          options={options}
          handleSelect={handleSelection}
          onChange={(e) => handleSelection(e.target.value)}
          setSelectedVals={(vals) => {
            const rawQuestion = rawQuestions[question.id] || question;
            modifyQuestion(question.id, { ...rawQuestion, inputValue: vals });
          }}
          setFormData={addFiles}
          removeAttachements={removeFiles}
          setHasSelectedSection={setHasSelectedSection}
        />
        {(!question.inputValue || isCheckboxEmpty) && question.isRequired && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: 'block', fontWeight: 700 }}
          >
            {t('formControl.fieldRequired')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
